import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { 
  QrCode, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Package, 
  User, 
  MapPin, 
  AlertTriangle,
  ArrowLeft 
} from 'lucide-react'
import { verifyQRCode, markQRCodeUsed, QRCodeVerification } from '../lib/qrcode'
import { formatPrice } from '../lib/square'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'

const QRVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const [verification, setVerification] = useState<QRCodeVerification | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [completed, setCompleted] = useState(false)

  useEffect(() => {
    const verifyCode = async () => {
      try {
        setLoading(true)
        const result = await verifyQRCode(searchParams)
        setVerification(result)
      } catch (error) {
        console.error('Verification failed:', error)
        setVerification({
          isValid: false,
          error: 'Failed to verify QR code'
        })
      } finally {
        setLoading(false)
      }
    }

    verifyCode()
  }, [searchParams])

  const handleMarkAsPickedUp = async () => {
    if (!verification?.isValid || !verification.order) return

    const orderId = searchParams.get('o')
    const token = searchParams.get('token')
    
    if (!orderId || !token) {
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Invalid Code"
          message="The QR code parameters are invalid"
          type="error"
          visible={t.visible}
        />
      ))
      return
    }

    setProcessing(true)
    try {
      const success = await markQRCodeUsed(orderId, token)
      
      if (success) {
        setCompleted(true)
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            title="Pickup Complete"
            message="Order has been marked as picked up"
            type="success"
            visible={t.visible}
          />
        ))
      } else {
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            title="Update Failed"
            message="Could not mark order as picked up"
            type="error"
            visible={t.visible}
          />
        ))
      }
    } catch (error) {
      console.error('Failed to mark as picked up:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Processing Failed"
          message="Could not process pickup. Please try again"
          type="error"
          visible={t.visible}
        />
      ))
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen-safe bg-secret-bg-primary flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="text-secret-text-secondary mt-4">Verifying QR code...</p>
        </div>
      </div>
    )
  }

  if (!verification) {
    return (
      <div className="min-h-screen-safe bg-secret-bg-primary flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <XCircle size={64} className="text-red-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-secret-text-primary mb-4">Verification Failed</h1>
          <p className="text-secret-text-secondary mb-6">
            Unable to verify this QR code. Please try scanning again.
          </p>
          <button
            onClick={() => navigate('/')}
            className="secret-btn-primary"
          >
            Return Home
          </button>
        </div>
      </div>
    )
  }

  if (!verification.isValid) {
    return (
      <div className="min-h-screen-safe bg-secret-bg-primary flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          {verification.alreadyUsed ? (
            <>
              <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-secret-text-primary mb-4">Already Picked Up</h1>
              <p className="text-secret-text-secondary mb-6">
                This order has already been picked up. Each QR code can only be used once.
              </p>
            </>
          ) : (
            <>
              <XCircle size={64} className="text-red-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-secret-text-primary mb-4">Invalid QR Code</h1>
              <p className="text-secret-text-secondary mb-6">
                {verification.error || 'This QR code is not valid or has expired.'}
              </p>
            </>
          )}
          
          <button
            onClick={() => navigate('/')}
            className="secret-btn-primary flex items-center space-x-2 mx-auto"
          >
            <ArrowLeft size={16} />
            <span>Return Home</span>
          </button>
        </div>
      </div>
    )
  }

  if (completed) {
    return (
      <div className="min-h-screen-safe bg-secret-bg-primary flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-secret-text-primary mb-4">Pickup Complete!</h1>
          <p className="text-secret-text-secondary mb-6">
            Order #{verification.order?.order_number} has been successfully picked up.
          </p>
          <div className="bg-secret-bg-secondary rounded-lg p-4 mb-6">
            <p className="text-secret-text-primary">
              Thank you for choosing SECRET! We hope you enjoy your order.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="secret-btn-primary"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  const { order, event } = verification

  return (
    <div className="min-h-screen-safe bg-secret-bg-primary">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <QrCode size={48} className="text-secret-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-secret-text-primary mb-2">Order Verification</h1>
          <p className="text-secret-text-secondary">
            Verify this order is ready for pickup
          </p>
        </div>

        {/* Order Details */}
        <div className="bg-secret-bg-secondary rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-secret-text-primary flex items-center space-x-2">
              <Package size={20} />
              <span>Order #{order.order_number}</span>
            </h2>
            <div className="text-secret-primary font-bold text-lg">
              {formatPrice(order.total_amount * 100)}
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-secret-bg-tertiary rounded-lg p-4 mb-4">
            <h3 className="font-medium text-secret-text-primary mb-2 flex items-center space-x-2">
              <User size={16} />
              <span>Customer</span>
            </h3>
            <div className="text-secret-text-secondary text-sm">
              <div>Order placed: {new Date(order.created_at).toLocaleString()}</div>
              <div>Status: <span className="text-secret-text-primary capitalize">{order.status}</span></div>
            </div>
          </div>

          {/* Event Info */}
          {event && (
            <div className="bg-secret-bg-tertiary rounded-lg p-4 mb-4">
              <h3 className="font-medium text-secret-text-primary mb-2 flex items-center space-x-2">
                <MapPin size={16} />
                <span>Event Details</span>
              </h3>
              <div className="text-sm">
                <div className="text-secret-text-primary font-medium">{event.title}</div>
                <div className="text-secret-text-secondary">{event.location_name}</div>
                <div className="text-secret-text-secondary">
                  {new Date(event.event_date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </div>
                <div className="text-secret-text-secondary">
                  Pickup: {event.pickup_start_time} - {event.pickup_end_time}
                </div>
              </div>
            </div>
          )}

          {/* Order Items */}
          {order.secret_order_items && order.secret_order_items.length > 0 && (
            <div className="bg-secret-bg-tertiary rounded-lg p-4">
              <h3 className="font-medium text-white mb-3">Order Items</h3>
              <div className="space-y-3">
                {order.secret_order_items.map((item: any, index: number) => (
                  <div key={index} className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="text-white font-medium">
                        {item.secret_menu_items?.name || 'Item'}
                      </div>
                      <div className="text-secret-text-secondary text-sm">
                        Qty: {item.quantity} × {formatPrice(item.unit_price * 100)}
                      </div>
                    </div>
                    <div className="text-white font-medium ml-4">
                      {formatPrice(item.total_price * 100)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Status Check */}
        {order.status !== 'ready' && order.status !== 'completed' && (
          <div className="bg-yellow-600/20 border border-yellow-600 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle size={16} className="text-yellow-500" />
              <span className="font-medium text-yellow-400">Order Not Ready</span>
            </div>
            <p className="text-yellow-200 text-sm">
              This order is currently "{order.status}" and not ready for pickup. 
              Please wait until the order status is "ready" before attempting pickup.
            </p>
          </div>
        )}

        {/* Pickup Button */}
        {(order.status === 'ready' || order.status === 'completed') && (
          <div className="bg-green-600/20 border border-green-600 rounded-lg p-6">
            <div className="text-center">
              <CheckCircle size={32} className="text-green-400 mx-auto mb-4" />
              <h3 className="font-bold text-white mb-2">Ready for Pickup</h3>
              <p className="text-green-200 text-sm mb-6">
                This order is ready and verified. Click below to mark as picked up.
              </p>
              
              <button
                onClick={handleMarkAsPickedUp}
                disabled={processing}
                className="secret-btn-primary w-full flex items-center justify-center space-x-2"
              >
                {processing ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Package size={16} />
                    <span>Mark as Picked Up</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="secret-btn-secondary flex items-center space-x-2 mx-auto"
          >
            <ArrowLeft size={16} />
            <span>Back</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default QRVerificationPage